import React from "react";
import { Box, Typography, keyframes, useTheme, alpha } from "@mui/material";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const spinReverse = keyframes`
  from { transform: rotate(360deg); }
  to { transform: rotate(0deg); }
`;

const breathe = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.85; }
  50% { transform: scale(1.08); opacity: 1; }
`;

const corePulse = keyframes`
  0%, 100% {
    box-shadow:
      0 0 24px var(--loader-glow),
      0 0 48px var(--loader-glow-soft),
      inset 0 0 20px rgba(255,255,255,0.12);
  }
  50% {
    box-shadow:
      0 0 40px var(--loader-glow),
      0 0 80px var(--loader-glow-soft),
      inset 0 0 28px rgba(255,255,255,0.2);
  }
`;

const orbitDash = keyframes`
  0% { stroke-dashoffset: 320; opacity: 0.35; }
  50% { opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 0.35; }
`;

const barSlide = keyframes`
  0% { transform: translateX(-120%); }
  100% { transform: translateX(320%); }
`;

const textGlow = keyframes`
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
`;

const particleFloat = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.2; }
  50% { transform: translate(var(--dx), var(--dy)) scale(1.4); opacity: 0.9; }
`;

const STATUS_LINES = [
  "Initializing neural pipeline",
  "Syncing creative engines",
  "Rendering your experience",
  "Polishing the magic",
];

const PARTICLES = [
  { top: "18%", left: "22%", dx: "8px", dy: "-14px", delay: 0 },
  { top: "28%", right: "18%", dx: "-10px", dy: "-8px", delay: 0.4 },
  { bottom: "32%", left: "16%", dx: "12px", dy: "10px", delay: 0.8 },
  { bottom: "24%", right: "20%", dx: "-6px", dy: "12px", delay: 1.2 },
  { top: "42%", left: "8%", dx: "6px", dy: "6px", delay: 0.6 },
  { top: "38%", right: "10%", dx: "-8px", dy: "4px", delay: 1 },
];

/**
 * Full-screen global loader (default) or compact inline block.
 * @param {{ fullScreen?: boolean, compact?: boolean, message?: string, brandName?: string }} props
 */
const LoadingComp = ({
  fullScreen = true,
  compact = false,
  message,
  brandName = "Beyund AI",
  showBrand = true,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || theme.palette.info.main;
  const accent = theme.palette.info?.main || "#06B6D4";

  const [statusIdx, setStatusIdx] = React.useState(0);

  React.useEffect(() => {
    if (message) return undefined;
    const id = setInterval(() => {
      setStatusIdx((i) => (i + 1) % STATUS_LINES.length);
    }, 2400);
    return () => clearInterval(id);
  }, [message]);

  const statusText = message || STATUS_LINES[statusIdx];
  const size = compact ? 88 : 140;
  const ringSize = compact ? size + 36 : size + 56;

  const shellSx = fullScreen
    ? {
        minHeight: "100vh",
        width: "100%",
        bgcolor: isDark ? "#030712" : "#f8fafc",
      }
    : {
        minHeight: compact ? 120 : 280,
        width: "100%",
        py: compact ? 2 : 4,
      };

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={statusText}
      sx={{
        ...shellSx,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        "--loader-glow": alpha(primary, 0.65),
        "--loader-glow-soft": alpha(secondary, 0.35),
      }}
    >
      {/* Ambient mesh */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: isDark
            ? `radial-gradient(ellipse 80% 60% at 50% 0%, ${alpha(primary, 0.22)} 0%, transparent 55%),
               radial-gradient(ellipse 60% 50% at 100% 100%, ${alpha(secondary, 0.18)} 0%, transparent 50%),
               radial-gradient(ellipse 50% 40% at 0% 80%, ${alpha(accent, 0.14)} 0%, transparent 45%)`
            : `radial-gradient(ellipse 80% 60% at 50% 0%, ${alpha(primary, 0.12)} 0%, transparent 55%),
               radial-gradient(ellipse 60% 50% at 100% 100%, ${alpha(secondary, 0.1)} 0%, transparent 50%),
               radial-gradient(ellipse 50% 40% at 0% 80%, ${alpha(accent, 0.08)} 0%, transparent 45%)`,
          pointerEvents: "none",
        }}
      />

      {/* Grid */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: isDark ? 0.35 : 0.2,
          backgroundImage: `
            linear-gradient(${alpha(primary, 0.08)} 1px, transparent 1px),
            linear-gradient(90deg, ${alpha(primary, 0.08)} 1px, transparent 1px)
          `,
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse 70% 60% at 50% 50%, black 20%, transparent 75%)",
          pointerEvents: "none",
        }}
      />

      {PARTICLES.map((p, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            width: compact ? 4 : 6,
            height: compact ? 4 : 6,
            borderRadius: "50%",
            bgcolor: i % 2 === 0 ? primary : secondary,
            top: p.top,
            left: p.left,
            right: p.right,
            bottom: p.bottom,
            "--dx": p.dx,
            "--dy": p.dy,
            animation: `${particleFloat} ${2.8 + i * 0.3}s ease-in-out infinite`,
            animationDelay: `${p.delay}s`,
            pointerEvents: "none",
          }}
        />
      ))}

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: compact ? 1.5 : 3,
        }}
      >
        {/* Orbital loader */}
        <Box
          sx={{
            position: "relative",
            width: ringSize,
            height: ringSize,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Outer conic ring */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: `conic-gradient(from 0deg, transparent 0deg, ${primary} 90deg, ${secondary} 180deg, ${accent} 270deg, transparent 360deg)`,
              animation: `${spin} 2.4s linear infinite`,
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 3,
                borderRadius: "50%",
                bgcolor: isDark ? "#030712" : "#f8fafc",
              },
            }}
          />

          {/* Middle dashed ring */}
          <Box
            component="svg"
            viewBox="0 0 120 120"
            sx={{
              position: "absolute",
              width: size + 28,
              height: size + 28,
              animation: `${spinReverse} 3.6s linear infinite`,
            }}
          >
            <circle
              cx="60"
              cy="60"
              r="52"
              fill="none"
              stroke={alpha(primary, 0.5)}
              strokeWidth="2"
              strokeDasharray="24 12"
              strokeLinecap="round"
            />
          </Box>

          {/* Inner orbit stroke */}
          <Box
            component="svg"
            viewBox="0 0 100 100"
            sx={{
              position: "absolute",
              width: size + 8,
              height: size + 8,
              animation: `${spin} 1.8s linear infinite`,
            }}
          >
            <Box
              component="circle"
              cx="50"
              cy="50"
              r="44"
              fill="none"
              stroke={`url(#loaderGrad-${isDark ? "d" : "l"})`}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray="180 140"
              sx={{ animation: `${orbitDash} 2s ease-in-out infinite` }}
            />
            <defs>
              <linearGradient id={`loaderGrad-${isDark ? "d" : "l"}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={primary} />
                <stop offset="50%" stopColor={secondary} />
                <stop offset="100%" stopColor={accent} />
              </linearGradient>
            </defs>
          </Box>

          {/* Core */}
          <Box
            sx={{
              width: size * 0.52,
              height: size * 0.52,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(145deg, ${primary} 0%, ${secondary} 55%, ${accent} 100%)`,
              animation: `${corePulse} 2s ease-in-out infinite, ${breathe} 3s ease-in-out infinite`,
              zIndex: 2,
            }}
          >
            <AutoAwesomeOutlined
              sx={{
                fontSize: compact ? 28 : 40,
                color: "#fff",
                filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.35))",
                animation: `${breathe} 2s ease-in-out infinite`,
              }}
            />
          </Box>
        </Box>

        {!compact && showBrand !== false && (
          <Typography
            variant="h5"
            fontWeight={800}
            letterSpacing="-0.03em"
            sx={{
              background: `linear-gradient(120deg, ${primary} 0%, ${secondary} 45%, ${accent} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              textAlign: "center",
            }}
          >
            {brandName}
          </Typography>
        )}

        <Typography
          variant={compact ? "caption" : "body2"}
          sx={{
            color: "text.secondary",
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            fontSize: compact ? "0.65rem" : "0.72rem",
            animation: `${textGlow} 2.4s ease-in-out infinite`,
            minHeight: compact ? 16 : 20,
            textAlign: "center",
            px: 2,
          }}
        >
          {statusText}
        </Typography>

        {/* Progress track */}
        <Box
          sx={{
            width: compact ? 120 : 200,
            height: 4,
            borderRadius: 99,
            bgcolor: alpha(primary, isDark ? 0.15 : 0.1),
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "40%",
              height: "100%",
              borderRadius: 99,
              background: `linear-gradient(90deg, transparent, ${primary}, ${secondary}, transparent)`,
              animation: `${barSlide} 1.4s ease-in-out infinite`,
            }}
          />
        </Box>
      </Box>

      {/* Bottom accent line */}
      {!compact && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "min(420px, 70vw)",
            height: 3,
            borderRadius: "99px 99px 0 0",
            background: `linear-gradient(90deg, transparent, ${primary}, ${secondary}, ${accent}, transparent)`,
            opacity: 0.7,
            filter: "blur(0.5px)",
          }}
        />
      )}
    </Box>
  );
};

/** Centered loader for page sections (replaces bare CircularProgress). */
export function InlineLoader({ message, sx }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 6, ...sx }}>
      <LoadingComp fullScreen={false} compact message={message} showBrand={false} />
    </Box>
  );
}

export default LoadingComp;
